import React, { useState } from "react";
import { ChevronDown, Loader2 } from "lucide-react";
import { EditableText, EditableItem } from "./lp-editor";

/**
 * FAQ
 * ───
 * Self-contained FAQ accordion (no props), modeled on the LP Express
 * renderer's FaqSection. Static Papabear copy.
 */
const FAQ_ITEMS = [
  {
    question: "How is this different from drugstore skincare?",
    answer:
      "No fillers, no synthetic fragrance, no water padding the bottle. Every jar is whole-food ingredients — grass-fed tallow, raw honey, beeswax, and cold-pressed oils — made fresh in small batches.",
  },
  {
    question: "How fast will I see results?",
    answer:
      "Most customers notice smoother, more comfortable skin within the first two weeks. Dryness and rough patches keep improving over 30 to 90 days of consistent use.",
  },
  {
    question: "Is the membership a contract?",
    answer:
      "No. Skip, pause, or cancel anytime — no contracts, no commitments, no fine print. Refills ship every 45 days at the locked-in member price.",
  },
  {
    question: "What's your guarantee?",
    answer:
      "Every order is backed by a 45-day money-back guarantee. If it isn't right for your skin, we'll make it right.",
  },
  {
    question: "Where is it made and shipped from?",
    answer:
      "Hand-made on our ranch in Temecula, California and shipped fresh within days of production. Free U.S. shipping on the starter kit.",
  },
];

export function FAQ({
  faqs,
  loading = false,
}: {
  faqs?: Array<{ question?: string; answer?: string; hidden?: boolean }>;
  loading?: boolean;
}) {
  const [openIdx, setOpenIdx] = useState<number | null>(0);

  const hasAiContent = !!faqs?.some(
    (f) => (f?.question ?? "").trim() || (f?.answer ?? "").trim(),
  );
  const showFaqSpinner = loading && !hasAiContent;

  return (
    <section className="bg-white py-12 md:py-20 px-4 sm:px-6" data-testid="lp-faq">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="font-black text-[26px] md:text-[32px] text-[var(--color-brand-text)] inline-flex items-center justify-center gap-2">
            <EditableText
              as="span"
              path={["faq", "section_headline"]}
              value=""
              placeholder="Frequently asked questions"
            />
            {showFaqSpinner && <Loader2 className="h-5 w-5 animate-spin text-[var(--color-brand-navy)] opacity-50" data-testid="loading-faq" />}
            {(() => {
              const isWorkflow = hasAiContent;
              const color = isWorkflow ? "#22C55E" : "#EF4444";
              const title = isWorkflow ? "Generated by an AI workflow" : "Static placeholder content";
              return (
                <span
                  title={title}
                  aria-label={title}
                  data-testid={`dot-source-${isWorkflow ? "workflow" : "placeholder"}`}
                  className="inline-block h-2 w-2 rounded-full shrink-0"
                  style={{ backgroundColor: color, boxShadow: `0 0 0 3px ${color}2e` }}
                />
              );
            })()}
          </h2>
        </div>
        <div>
          {hasAiContent
            ? faqs!.map((f, i) => {
                const q = (f?.question ?? "").trim();
                const a = (f?.answer ?? "").trim();
                if (!q && !a) return null;
                const open = openIdx === i;
                return (
                  <EditableItem
                    key={i}
                    path={["faq", "faqs"]}
                    index={i}
                    count={faqs!.length}
                    hidden={f?.hidden}
                    allowDelete
                    testId={`faq-${i}`}
                  >
                    <div
                      className="border-t border-[#D8CFC3] last:border-b"
                      data-testid={`lp-faq-item-${i}`}
                    >
                      <button
                        type="button"
                        onClick={() => setOpenIdx(open ? null : i)}
                        className="w-full flex items-center justify-between gap-4 py-5 text-left"
                        aria-expanded={open}
                        data-testid={`button-lp-faq-${i}`}
                      >
                        <EditableText
                          as="span"
                          noHideDelete
                          path={["faq", "faqs", i, "question"]}
                          value={q}
                          placeholder=""
                          className="text-xs font-black uppercase tracking-widest text-[var(--color-brand-navy)] font-sans"
                        />
                        <span
                          className={`shrink-0 h-6 w-6 rounded-full border-2 border-[var(--color-brand-navy)] flex items-center justify-center transition-transform duration-200 ${
                            open ? "rotate-180" : ""
                          }`}
                        >
                          <ChevronDown className="h-3.5 w-3.5 text-[var(--color-brand-navy)]" />
                        </span>
                      </button>
                      {open && (
                        <EditableText
                          as="p"
                          multiline
                          path={["faq", "faqs", i, "answer"]}
                          value={a}
                          placeholder=""
                          className="pb-5 text-xs text-[#6B6560] leading-relaxed"
                          testId={`lp-faq-answer-${i}`}
                        />
                      )}
                    </div>
                  </EditableItem>
                );
              })
            : FAQ_ITEMS.map((it, i) => {
                const open = openIdx === i;
                return (
                  <div
                    key={i}
                    className="border-t border-[#D8CFC3] last:border-b"
                    data-testid={`lp-faq-item-${i}`}
                  >
                    <button
                      type="button"
                      onClick={() => setOpenIdx(open ? null : i)}
                      className="w-full flex items-center justify-between gap-4 py-5 text-left"
                      aria-expanded={open}
                      data-testid={`button-lp-faq-${i}`}
                    >
                      <EditableText
                        as="span"
                        noHideDelete
                        path={["faq", "faqs", i, "question"]}
                        value={it.question}
                        placeholder=""
                        className="text-xs font-black uppercase tracking-widest text-[var(--color-brand-navy)] font-sans"
                      />
                      <span
                        className={`shrink-0 h-6 w-6 rounded-full border-2 border-[var(--color-brand-navy)] flex items-center justify-center transition-transform duration-200 ${
                          open ? "rotate-180" : ""
                        }`}
                      >
                        <ChevronDown className="h-3.5 w-3.5 text-[var(--color-brand-navy)]" />
                      </span>
                    </button>
                    {open && (
                      <EditableText
                        as="p"
                        multiline
                        path={["faq", "faqs", i, "answer"]}
                        value={it.answer}
                        placeholder=""
                        className="pb-5 text-xs text-[#6B6560] leading-relaxed"
                        testId={`lp-faq-answer-${i}`}
                      />
                    )}
                  </div>
                );
              })}
        </div>
      </div>
    </section>
  );
}
